import {
  Component,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  ViewEncapsulation,
  afterRenderEffect,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { normaliseProse } from '../../../core/utils/render-rich-text.util';

/**
 * Raw-HTML block — renders arbitrary CMS-supplied HTML via [innerHTML] and,
 * critically, re-executes any embedded <script> tags by cloning them via
 * doc.createElement. Per HTML spec, <script> tags set through innerHTML are
 * inert; the browser only executes scripts that enter the DOM via
 * createElement + appendChild (see STATE.md decisions 24-03 / 27-02, and
 * core/seo/jsonld/inject.ts for the canonical project pattern).
 *
 * Re-execution: an afterRenderEffect re-runs `executeEmbeddedScripts` after
 * EVERY render whose content changed (the effect reads `trusted()` to register
 * a reactive dependency on the html input). This matters because the
 * RawHtmlBlockComponent instance is REUSED across CMS-page SPA navigations
 * (catch-all ** route + positional @for tracking) — a one-shot afterNextRender
 * would only fire for the FIRST page that mounted this slot, leaving a later
 * page's embedded <script> (e.g. the /contact intent widget) dead. Idempotency:
 * each processed <script> is marked with data-raw-html-exec="1"; because the
 * innerHTML replacement wipes the old script nodes (and their markers) together
 * with the old content, fresh content re-executes exactly once and a same-
 * content re-render does not double-execute. Scripts are SSR-safe because
 * afterRenderEffect (like afterNextRender) only fires client-side.
 *
 * Image lazy-loading contract (quick-84): the input HTML string is piped
 * through `normaliseProse` (render-rich-text.util.ts:75-96) BEFORE the
 * sanitizer-bypass, so any CMS-authored <img> past the first per block gains
 * `loading="lazy" decoding="async"` — same first-image LCP carve-out as every
 * other CMS-body surface (callout-block, text-block, table-block, tab-block,
 * timeline-block, faq-item, cards-block) routed via `renderRichText`. This is
 * the canonical Phase-42 A11Y-02/05 contract; do NOT inline a duplicate
 * regex. See render-rich-text.util.ts:75-96 for the source-of-truth transform
 * and Phase 42 A11Y-02/05 for the contract acceptance.
 */
@Component({
  selector: 'ds-raw-html-block',
  template: '<div [innerHTML]="trusted()"></div>',
  encapsulation: ViewEncapsulation.None,
})
export class RawHtmlBlockComponent {
  readonly html = input.required<string>();
  private readonly sanitizer = inject(DomSanitizer);
  private readonly normalisedHtml = computed(() => normaliseProse(this.html()));
  readonly trusted = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(this.normalisedHtml()),
  );

  constructor() {
    const doc = inject(DOCUMENT);
    if (isPlatformServer(inject(PLATFORM_ID))) {
      effect(() => preloadPriorityImage(doc, this.normalisedHtml()));
    }
    const hostEl = inject(ElementRef<HTMLElement>).nativeElement as HTMLElement;
    const destroyed = { value: false };
    inject(DestroyRef).onDestroy(() => {
      destroyed.value = true;
    });

    afterRenderEffect(() => {
      // Read trusted() to register a reactive dependency so this re-runs on
      // every content change — NOT just the first render. Critical: the
      // CmsPageComponent instance (and therefore this RawHtmlBlockComponent)
      // is REUSED across CMS-page SPA navigations (catch-all ** route +
      // positional @for tracking), so a one-shot afterNextRender fires only
      // for the FIRST page that mounted this slot. When the html input later
      // swaps to a page whose raw-html block carries an embedded <script>
      // (e.g. the /contact intent widget), the inert innerHTML <script> must
      // be re-injected — that is this effect's job.
      this.trusted();
      if (destroyed.value) return;
      executeEmbeddedScripts(doc, hostEl);
    });
  }
}

/** Advertise an explicitly prioritised CMS image before the browser parses the body.
 * Use the rendered responsive candidates so mobile does not preload a different size.
 * Ordinary/lazy images and picture art direction retain their existing loading behavior.
 */
export function preloadPriorityImage(doc: Document, html: string): void {
  if (!html.toLowerCase().includes('fetchpriority')) return;
  if (doc.head.querySelector('link[rel="preload"][as="image"]')) return;
  const container = doc.createElement('div');
  container.innerHTML = html;
  const img = Array.from(container.querySelectorAll('img')).find(image =>
    image.getAttribute('fetchpriority')?.toLowerCase() === 'high'
    && image.getAttribute('loading')?.toLowerCase() !== 'lazy'
    && !image.closest('picture')
    && image.hasAttribute('src'),
  );
  const src = img?.getAttribute('src');
  if (!img || !src) return;
  const link = doc.createElement('link');
  link.setAttribute('rel', 'preload');
  link.setAttribute('as', 'image');
  link.setAttribute('href', src);
  link.setAttribute('fetchpriority', 'high');
  for (const [from, to] of [
    ['srcset', 'imagesrcset'], ['sizes', 'imagesizes'],
    ['crossorigin', 'crossorigin'], ['referrerpolicy', 'referrerpolicy'],
  ]) {
    const value = img.getAttribute(from);
    if (value !== null) link.setAttribute(to, value);
  }
  doc.head.insertBefore(link, doc.head.querySelector('link[rel="stylesheet"], style'));
}

/**
 * Walk the host element, find every <script> not yet marked as executed,
 * and re-inject it via doc.createElement. Marker prevents double execution.
 * Exported for unit testing.
 */
export function executeEmbeddedScripts(doc: Document, host: HTMLElement): void {
  const scripts = Array.from(host.querySelectorAll('script')) as HTMLScriptElement[];
  for (const old of scripts) {
    if (old.dataset['rawHtmlExec'] === '1') continue;
    const fresh = doc.createElement('script');
    // Copy attributes (src, type, async, data-*, etc.)
    for (const { name, value } of Array.from(old.attributes)) {
      fresh.setAttribute(name, value);
    }
    // Inline scripts: copy text content.
    if (!old.src && old.textContent) {
      fresh.textContent = old.textContent;
    }
    fresh.dataset['rawHtmlExec'] = '1';
    // Replace the inert node in-place so subsequent querySelectorAll calls
    // do not re-see the original, and relative DOM positions are preserved.
    old.parentNode?.replaceChild(fresh, old);
  }
}
